<!-- FileTreeItem.vue -->
<template>
  <div class="file-tree-wrapper">
    <div 
      class="file-item"
      :class="{ 
        active: isActive,
        'folder-item': file.isDirectory 
      }"
      @click="handleClick"
    >
      <!-- 文件图标（仅文件） -->
      <i 
        v-if="!file.isDirectory"
        class="file-icon"
        :class="getFileIconClass(file.name)"
      ></i>
      
      <!-- 文件夹图标（关闭状态） -->
      <i 
        v-if="file.isDirectory && !isExpanded"
        class="file-icon folder-closed"
      ></i>
      
      <!-- 文件夹图标（打开状态） -->
      <i 
        v-if="file.isDirectory && isExpanded"
        class="file-icon folder-open"
      ></i>
      
      <!-- 文件名显示或编辑 -->
      <template v-if="!isRenaming">
        <span class="file-name">{{ file.name }}</span>
      </template>
      
      <!-- 重命名输入框 -->
      <template v-else>
        <el-input
          ref="renameInput"
          v-model="newName"
          size="mini"
          class="rename-input"
          @keyup.enter.native="confirmRename"
          @blur.native="cancelRename"
          @click.stop
        ></el-input>
      </template>
      
      <!-- 更多操作按钮 -->
      <el-popover
        placement="right" 
        trigger="click"  
        :ref="`filePopover-${file.name}`" 
        width="150"        
        popper-class="file-popover-custom" 
        v-if="!isRenaming"
      >
        <div class="file-context-menu">
          <div class="menu-item" @click="handleDownload">
            <i class="el-icon-download"></i>
            <span>下载</span>
          </div>
          <div class="menu-item" @click="handleRename">
            <i class="el-icon-edit"></i>
            <span>重命名</span>
          </div>
          <div class="menu-item" @click="handleMove">
            <i class="el-icon-rank"></i>
            <span>移动</span>
          </div>
          <div class="menu-item delete-item" @click="handleDelete">
            <i class="el-icon-delete"></i>
            <span>删除</span>
          </div>
        </div>
        <i 
          slot="reference"
          class="el-icon-more-outline file-more-trigger"
          @click.stop 
        ></i>
      </el-popover>
    </div>
    
    <!-- 递归渲染子文件夹内容 -->
    <div v-if="file.isDirectory && isExpanded" class="folder-children">
      <div v-if="loading" class="loading-children">
        加载中...
      </div>
      <template v-else>
        <file-tree-item
          v-for="(childFile, index) in children"
          :key="childFile.name + '_' + index"
          :file="childFile"
          :expanded-folders="expandedFolders"
          :current-dir="fullPath"
          @select="handleSelect"
          @toggle="handleToggle"
          @download="handleChildDownload"
          @rename="handleChildRename"
          @move="handleChildMove"
          @delete="handleChildDelete"
          @refresh="handleRefresh"
          @refresh-folder="handleRefreshFolder"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { file_list_path } from '@/js/api_paths';

export default {
  name: 'FileTreeItem',
  props: {
    file: {
      type: Object,
      required: true
    },
    expandedFolders: {
      type: Set,
      required: true
    },
    currentDir: { // 当前组件位于什么目录下
      type: String,
      required: true
    }
  },
  data() {
    return {
      children: [],
      loading: false,
      isExpanded: false,
      isRenaming: false,
      newName: ''
    }
  },
  computed: {
    folderRefreshFlag() { // 文件夹刷新状态变量
      return this.$store.state.folderRefreshFlags[this.fullPath];
    },

    currentSelectedDir() {
        return this.$store.state.currentSelectedDir;
    },
    currentFilePath(){
        return this.$store.state.currentFilePath;
    },

    fullPath() { // 当前文件项目的完整相对路径, 可以用作项目的唯一标识  
      // 计算完整路径
      const separator = this.currentDir.endsWith('/') ? '' : '/';
      return this.currentDir + separator + this.file.name + (this.file.isDirectory ? '/' : '');
    },
    isActive() {
        if(this.file.isDirectory){ // 如果是文件夹
            return this.$store.state.currentSelectedType === 'dir' && this.currentSelectedDir === this.fullPath; 
        }
        // 判断当前文件是否被选中
        return this.$store.state.currentSelectedType === 'file' && this.currentFilePath === this.fullPath;
    }
  },
  watch: {
    isExpanded: {
      handler(newVal) {
        if (newVal && this.file.isDirectory && this.children.length === 0 && !this.loading) {
          this.loadChildren();
        }
      },
      immediate: true
    },
    folderRefreshFlag: { // 监听刷新控制变量的变化
      handler() {
        this.loadChildren();
      },
      immediate: true
    },

  },
  mounted(){
      this.isExpanded = this.expandedFolders.has(this.fullPath); 
  },
  methods: {
    handleClick() {
      if (this.file.isDirectory) {
        // 点击文件夹时切换展开状态
        this.toggleFolder();
      } else {
        // 点击文件时选中
        this.$emit('select', {
          currentDir: this.currentDir,
          filePath: this.fullPath
        });
      }
    },
    toggleFolder() {
        // 通知父组件切换文件夹展开状态
        this.$emit('toggle', this.fullPath);
        // 使用 nextTick 在下次 DOM 更新后执行
        this.$nextTick(() => {
            this.isExpanded = this.expandedFolders.has(this.fullPath);
        });
    },
    async loadChildren() {
      if (!this.file.isDirectory) return;
      this.loading = true;
      try {
        const token = this.$store.state.token;
        const req = {
          "relative_dir": this.fullPath
        };
        
        const response = await fetch(file_list_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(req)
        });
        
        const data = await response.json();
        if (data.status === "success") {
          this.children = data.msg || [];
        }
      } catch (error) {
        console.error('加载子文件失败:', error);
        this.children = [];
      } finally {
        this.loading = false;
      }
    },
    getFileIconClass(filename) {
      // 根据文件扩展名返回对应的图标类
      const ext = filename.split('.').pop().toLowerCase();
      const iconMap = {
        'md': 'markdown',
        'markdown': 'markdown',
        'txt': 'text',
        'pdf': 'pdf',
        'jpg': 'image',
        'jpeg': 'image',
        'png': 'image',
        'gif': 'image',
        'svg': 'image',
        'webp': 'image'
      };
      
      const type = iconMap[ext] || 'text';
      return `icon-${type}`;
    },
    // 事件处理方法
    handleSelect(msg) {
      this.$emit('select', {
        currentDir: msg.currentDir,
        filePath: msg.filePath
      });
    },
    handleToggle(folderPath) {
      this.$emit('toggle', folderPath);
    },
    handleDownload() {
      // 关闭 popover
      if (this.$refs[`filePopover-${this.file.name}`]) {
        this.$refs[`filePopover-${this.file.name}`].doClose();
      }
      this.$emit('download', this.fullPath);
    },
    
    handleRename() {
      // 关闭 popover
      if (this.$refs[`filePopover-${this.file.name}`]) {
        this.$refs[`filePopover-${this.file.name}`].doClose();
      }
      // 开始重命名
      this.isRenaming = true;
      this.newName = this.file.name;

      // 清理可能存在的旧监听器
      this.cleanupGlobalListener();
      // 添加全局点击监听器
      this.globalClickListener = (event) => {
        // 检查点击的元素是否在当前组件内
        if (!this.$el.contains(event.target)) {
          this.cancelRename();
        }
      };
      // 延迟添加监听器，避免立即触发
      setTimeout(() => {
        document.addEventListener('click', this.globalClickListener);
      }, 100);
      
      // 聚焦到输入框
      this.$nextTick(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput.focus();
        }
      });
    },

    confirmRename() {
      if (this.newName.trim() === '') {
        this.$message({
          type: 'warning',
          message: '文件名不能为空',
          duration: 1500
        });
        this.cancelRename();
        return;
      }
      
      if (this.newName === this.file.name) {
        this.cancelRename();
        return;
      }
      
      // 构造新的完整路径
      const separator = this.currentDir.endsWith('/') ? '' : '/';
      const newPath = this.currentDir + separator + this.newName + (this.file.isDirectory ? '/' : '');
      
      // 发送重命名事件给父组件
      this.$emit('rename', {
        oldPath: this.fullPath,
        newPath: newPath,
        currentDir: this.currentDir
      });
      this.newName = '';
      this.isRenaming = false;
      // 清理全局点击监听器
      this.cleanupGlobalListener();
    },

    cancelRename() {
      // 移除全局点击监听器
      this.cleanupGlobalListener();
      this.isRenaming = false;
      this.newName = '';
    },

    // 统一的清理函数
    cleanupGlobalListener() {
      if (this.globalClickListener) {
        document.removeEventListener('click', this.globalClickListener);
        this.globalClickListener = null;
      }
    },
    handleMove() {
      // 关闭 popover
      if (this.$refs[`filePopover-${this.file.name}`]) {
        this.$refs[`filePopover-${this.file.name}`].doClose();
      }
      // 存储源文件/文件夹路径
      this.$store.commit('SET_SOURCE_PATH', this.fullPath);
      // 清空目标目录
      this.$store.commit('SET_TARGET_DIR', '');
      // 让路径选择模态框可见
      this.$store.commit('SET_SHOW_MOVE_MODAL', true);
    },
    handleDelete() {
      // 关闭 popover
      if (this.$refs[`filePopover-${this.file.name}`]) {
        this.$refs[`filePopover-${this.file.name}`].doClose();
      }
      this.$emit('delete', {
        currentDir: this.currentDir,
        fullPath: this.fullPath
      });
    },
    handleRefresh() {
      this.$emit('refresh');
    },
    handleRefreshFolder(folderPath) {
      this.$emit('refresh-folder', folderPath);
    },
    // 子组件事件转发
    handleChildDownload(fileInfo) {
      this.$emit('download', fileInfo);
    },
    handleChildRename(fileInfo) {
      this.$emit('rename', fileInfo);
    },
    handleChildMove(fileInfo) {
      this.$emit('move', fileInfo);
    },
    handleChildDelete(msg) {
      this.$emit('delete', msg);
    }
  },

  // 组件销毁前清理
  beforeDestroy() {
    this.cleanupGlobalListener();
  },
}
</script>
<style scoped>
.file-tree-wrapper {
  width: 100%;
}

.folder-item {
  cursor: pointer;
}

.expand-icon {
  margin-right: 5px;
  color: #909399;
  cursor: pointer;
  width: 16px;
  display: inline-block;
}

.folder-children {
  padding-left: 20px;
}

.loading-children {
  padding: 5px 10px;
  color: #909399;
  font-size: 12px;
}

.file-icon {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  display: inline-block;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.file-item, .upload-btn {
  animation: fadeIn 0.3s ease-out;
}
.file-item:hover .file-more-trigger {
  opacity: 1;
}
.file-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  margin-bottom: 2px;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: transparent;
  border: none;
  box-shadow: none;
}
.file-item:hover {
  background: rgba(237, 242, 247, 0.9);
  transform: none;
  box-shadow: none;
}

/* 文件被选中时的高亮样式 */
.file-item.active:not(.folder-item) {
  background: linear-gradient(135deg, var(--primary-gradient-start, #667eea) 0%, var(--primary-gradient-end, #764ba2) 100%);
  color: white;
  border: none;
  box-shadow: none;
}

/* 文件夹被选中时的高亮样式（更浅的紫色） */
.file-item.active.folder-item {
  background: linear-gradient(135deg, #d8bfd8 0%, #cba0e6 100%);
  color: white;
  border: none;
  box-shadow: none;
}

.file-item.active .delete-btn {
  color: white;
  border-color: white;
}

/* 现代编辑器风格 - 左侧选中指示器 */
.file-item.active:not(.folder-item)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #667eea;
  border-radius: 0 2px 2px 0;
}

.file-item.active.folder-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #d8bfd8;
  border-radius: 0 2px 2px 0;
}

/* 现代编辑器风格的文件名样式 */
.file-name {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.2px;
  color: #2c3e50;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-item.active .file-name {
  color: white;
  font-weight: 500;
}

.file-item:hover .file-name {
  color: #1a2a3a;
}

/* 重命名输入框样式 */
.rename-input {
  flex: 1;
  margin-right: 8px;
}

.rename-input ::v-deep .el-input__inner {
  height: 24px;
  font-size: 14px;
  padding: 2px 8px;
}

/* 文件夹关闭状态图标 */
.file-icon.folder-closed {
  background-image: url('@/assets/icons/folder.svg');
}

/* 文件夹打开状态图标 */
.file-icon.folder-open {
  background-image: url('@/assets/icons/folder-open.svg');
}

/* 文件图标样式 */
.file-icon.icon-markdown {
  background-image: url('@/assets/icons/markdown.svg');
}

.file-icon.icon-text {
  background-image: url('@/assets/icons/text.svg');
}

.file-icon.icon-pdf {
  background-image: url('@/assets/icons/pdf.svg');
}

.file-icon.icon-image {
  background-image: url('@/assets/icons/image.svg');
}
</style>